import React from 'react';
import { z } from 'zod';
import { safeUrl } from '../url';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Template advantages — faithful rewrite of legacy
// molecules/TemplateAdvantagesSection (Strapi sections.timeline-new-advantages).
// Centered eyebrow + heading over a two-column row: a list of pointer
// title/description pairs and an image. Alternating sections flip sides
// (`reversed`, set by the importer from the section's position).
export const templateAdvantagesSchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().min(1),
  linkText: z.string().optional(),
  linkUrl: safeUrl.optional(),
  imageUrl: z.string().optional(),
  reversed: z.boolean().default(false),
  pointers: z
    .array(z.object({ title: z.string().min(1), description: z.string().optional() }))
    .default([]),
});

type TemplateAdvantagesProps = z.infer<typeof templateAdvantagesSchema>;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3.2rem;
  max-width: 112rem;
  margin: 0 auto;
  padding: 6.2rem 2.4rem;

  @media (min-width: 62em) {
    gap: 8.7rem;
    padding: 12rem 0;
  }
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 500;
  font-size: 1.2rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  text-align: center;

  @media (min-width: 62em) {
    font-size: 1.5rem;
    letter-spacing: 4px;
  }
`;

const Heading = styled.h2`
  margin: 1.2rem 0 0;
  color: #1a1b1f;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 2.8rem;
  text-align: center;

  @media (min-width: 62em) {
    margin-top: 2.2rem;
    font-size: 4.8rem;
    letter-spacing: -2px;
  }
`;

const Row = styled.div<{ $reversed: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.4rem;
  align-items: center;

  @media (min-width: 62em) {
    grid-template-columns: 1fr 1fr;
    gap: 6.4rem;
  }
`;

const Points = styled.div<{ $reversed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2.8rem;
  order: 1;

  @media (min-width: 62em) {
    gap: 4.8rem;
    order: ${({ $reversed }) => ($reversed ? 1 : 0)};
  }
`;

const PointTitle = styled.p`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
  font-size: 2.2rem;

  @media (min-width: 62em) {
    font-size: 2.8rem;
  }
`;

const PointDesc = styled.p`
  margin: 0.8rem 0 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.6rem;

  @media (min-width: 62em) {
    margin-top: 1.6rem;
    font-size: 2rem;
  }
`;

const LearnMore = styled.a`
  margin-top: 1.6rem;
  color: #0a3aa8;
  font-family: 'DM Sans', sans-serif;
  font-weight: 550;
  font-size: 1.6rem;
  letter-spacing: 0.64px;
  text-transform: uppercase;
  text-decoration: underline;
  text-underline-offset: 0.5rem;
`;

const AdvImageWrap = styled.div<{ $reversed: boolean }>`
  @media (min-width: 62em) {
    order: ${({ $reversed }) => ($reversed ? 0 : 1)};
  }
`;

const AdvImage = styled.img`
  width: 100%;
  max-width: 51rem;
  max-height: 56rem;
  border-radius: 2.4rem;
  object-fit: contain;
`;

function TemplateAdvantages({ eyebrow, heading, linkText, linkUrl, imageUrl, reversed, pointers }: TemplateAdvantagesProps) {
  return (
    <Section>
      <div>
        {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
        <Heading>{heading}</Heading>
      </div>
      <Row $reversed={reversed}>
        <Points $reversed={reversed}>
          {pointers.map((p, i) => (
            <div key={`${p.title}-${i}`}>
              <PointTitle>{p.title}</PointTitle>
              {p.description ? <PointDesc>{p.description}</PointDesc> : null}
            </div>
          ))}
          {linkUrl ? <LearnMore href={linkUrl}>{linkText ?? 'Learn More'}</LearnMore> : null}
        </Points>
        {imageUrl ? (
          <AdvImageWrap $reversed={reversed}>
            <AdvImage src={imageUrl} alt="" />
          </AdvImageWrap>
        ) : null}
      </Row>
    </Section>
  );
}

export const templateAdvantagesBlock: BlockDefinition<TemplateAdvantagesProps> = {
  type: 'templateAdvantages',
  schema: templateAdvantagesSchema,
  Renderer: TemplateAdvantages,
};
